import { compileNumberFormat, DEFAULT_DUPLICATE_SETTINGS } from '@wodi/domain';
import { z } from 'zod';

import { reasonSchema } from './clinical-common.ts';

// Tenant and facility configuration for registration (plan §12). Where the product owner
// left a value open, Wodi builds the mechanism and keeps the value configurable and
// visibly provisional instead of guessing it.

// --- Patient and visit numbers (plan §2.1, §12 decision 5) ---------------------------------

/** Patient and visit numbers, and chart (file) numbers (docs/plans/phase2-medical-records-charts.md). */
export const SEQUENCE_KINDS = ['patient_number', 'visit_number', 'chart_number'] as const;
export type SequenceKind = (typeof SEQUENCE_KINDS)[number];

export const numberFormatSchema = z.object({
  facilityId: z.uuid(),
  kind: z.enum(SEQUENCE_KINDS),
  /** A template for `compileNumberFormat` in @wodi/domain, e.g. `{facility}-{seq:6}`. */
  format: z.string(),
  /** The next number that will be issued, as a decimal string (it is a bigint). */
  nextValue: z.string().regex(/^\d+$/),
  /** The next number rendered in this format, for the settings screen. */
  example: z.string(),
});

export type NumberFormatView = z.infer<typeof numberFormatSchema>;

/** Changing a format never rewrites numbers already issued. */
export const numberFormatInputSchema = z.object({
  facilityId: z.uuid(),
  kind: z.enum(SEQUENCE_KINDS),
  format: z
    .string()
    .trim()
    .min(1)
    .max(60)
    .superRefine((template, ctx) => {
      const compiled = compileNumberFormat(template);
      if (!compiled.ok) ctx.addIssue({ code: 'custom', message: compiled.code });
    }),
});

// --- Duplicate thresholds, required fields, audit window (plan §12 decisions 6, 9, 17) ----

/**
 * Fields a tenant may make mandatory at registration, on top of the ones that always are:
 * a given name, sex, and a date of birth or an estimated age.
 */
export const OPTIONAL_REQUIRED_FIELDS = [
  'family_name',
  'phone',
  'identifier',
  'residence',
  'next_of_kin',
] as const;
export type OptionalRequiredField = (typeof OPTIONAL_REQUIRED_FIELDS)[number];

export const registrationSettingsSchema = z.object({
  duplicates: z.object({
    /** Name similarity threshold, as a whole percentage (60 means 0.6). */
    nameSimilarityPercent: z.number().int().min(1).max(100),
    estimatedBirthDateToleranceYears: z.number().int().min(0).max(10),
    sharedPhoneRaisesCandidate: z.boolean(),
    /** Always true until the thresholds are confirmed; the screen says so. */
    provisional: z.literal(true),
  }),
  requiredFields: z.array(z.enum(OPTIONAL_REQUIRED_FIELDS)),
  /** A patient view is audited once per session within this many minutes (0: every view). */
  patientViewAuditWindowMinutes: z.number().int().min(0).max(720),
});

export type RegistrationSettingsView = z.infer<typeof registrationSettingsSchema>;

/** The defaults the database starts every tenant with. */
export const DEFAULT_REGISTRATION_SETTINGS = {
  duplicates: {
    nameSimilarityPercent: Math.round(DEFAULT_DUPLICATE_SETTINGS.nameSimilarityMin * 100),
    estimatedBirthDateToleranceYears: DEFAULT_DUPLICATE_SETTINGS.estimatedBirthDateToleranceYears,
    sharedPhoneRaisesCandidate: DEFAULT_DUPLICATE_SETTINGS.sharedPhoneRaisesCandidate,
    provisional: true,
  },
  requiredFields: [],
  patientViewAuditWindowMinutes: 30,
} as const satisfies RegistrationSettingsView;

export const updateRegistrationSettingsRequestSchema = z.object({
  duplicates: registrationSettingsSchema.shape.duplicates
    .omit({ provisional: true })
    .partial()
    .optional(),
  requiredFields: z.array(z.enum(OPTIONAL_REQUIRED_FIELDS)).optional(),
  patientViewAuditWindowMinutes: z.number().int().min(0).max(720).optional(),
});

// --- Local value sets (plan §12 decisions 8 and 10) ------------------------------------------

/**
 * Value sets a tenant configures because no national set is specified yet
 * (TODO(verify-spec): the Client Registry's sex and relationship codes).
 */
export const VALUE_SETS = ['patient_sex', 'relationship', 'queue_priority_flag'] as const;
export type ValueSet = (typeof VALUE_SETS)[number];

export const valueSetConceptSchema = z.object({
  id: z.uuid(),
  valueSet: z.enum(VALUE_SETS),
  system: z.string(),
  code: z.string(),
  display: z.string(),
  /** Short form, e.g. "F" for the banner's "F · 34 y". */
  abbreviation: z.string().nullable(),
  /** A local set not yet matched to a national one; shown as provisional. */
  provisional: z.boolean(),
  status: z.enum(['active', 'inactive']),
  sortOrder: z.number().int(),
});

export type ValueSetConceptView = z.infer<typeof valueSetConceptSchema>;

export const valueSetConceptInputSchema = z.object({
  valueSet: z.enum(VALUE_SETS),
  system: z.string().trim().min(1).max(200),
  code: z.string().trim().min(1).max(64),
  display: z.string().trim().min(1).max(200),
  abbreviation: z.string().trim().min(1).max(8).nullable().default(null),
  provisional: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(10_000).default(0),
});

// --- Consent wording (plan §12 decision 3) -----------------------------------------------

export const CONSENT_TEXT_LANGUAGES = ['en', 'sw'] as const;

/**
 * The exact words read to a patient before recording consent. Wodi ships none: until the
 * wording is approved (TODO(legal-verify)), a tenant has only what it enters here.
 */
export const consentTextSchema = z.object({
  id: z.uuid(),
  purpose: z.literal('hie_data_sharing'),
  version: z.string(),
  language: z.enum(CONSENT_TEXT_LANGUAGES),
  body: z.string(),
  status: z.enum(['active', 'retired']),
  createdAt: z.iso.datetime(),
});

export type ConsentTextView = z.infer<typeof consentTextSchema>;

/** A text is never edited: a new wording is a new version, and the old one is retired. */
export const consentTextInputSchema = z.object({
  purpose: z.literal('hie_data_sharing'),
  version: z.string().trim().min(1).max(40),
  language: z.enum(CONSENT_TEXT_LANGUAGES),
  body: z.string().trim().min(1).max(10_000),
});

// --- Sensitivity labels (plan §12 decision 15) ------------------------------------------

/**
 * Labels on sensitive records (HIV, mental health, SGBV and similar). The list is tenant
 * configuration: the seed's labels are marked TODO(clinical-verify) until the clinical
 * safety lead confirms them. A role sees a labelled record only when granted that label;
 * anyone else needs `records.break_glass` and a reason, and is audited.
 */
export const sensitivityLabelSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  display: z.string(),
  status: z.enum(['active', 'inactive']),
});

export type SensitivityLabelView = z.infer<typeof sensitivityLabelSchema>;

export const sensitivityLabelInputSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^[a-z][a-z0-9_]{1,39}$/, 'Lower-case letters, digits and underscores'),
  display: z.string().trim().min(1).max(80),
});

export const roleSensitivityLabelsRequestSchema = z.object({
  labelIds: z.array(z.uuid()).max(50),
});

export const breakGlassRequestSchema = z.object({ reason: reasonSchema });
